import type { RootState } from '@appStore/store.ts'

import { appSelectors } from '@appStore/reducers/app'
import timer from 'assets/image/icons/svg/timer.svg'
import { useEffect, useState } from 'react'
import { appDay } from 'shared/day.ts'
import { useAppSelector } from 'shared/hooks/store.ts'
import { useLatest } from 'shared/hooks/useLatest.ts'
import { useStopReloadSession } from 'shared/hooks/useStopReloadSession.ts'
import { LoadingButton } from 'shared/ui/index.ts'

import css from './Header.module.scss'

const enum Border {
	Reload = 30_000,
	/** 5 minutes */
	Show = 300_000
}

const selectExpired = (state: RootState) =>
	appSelectors.persona(state)?.accessTokenExpiresAt ?? ''

export function ImpersonatedTimer() {
	const expired = useAppSelector(selectExpired)
	const [logOut, { isLoading }] = useStopReloadSession()

	const [timerStarted, setTimerStarted] = useState(false)

	const logOutRef = useLatest(logOut)

	useEffect(() => {
		if (expired) {
			const diff = appDay(expired).diff(Date.now(), 'ms')
			if (diff <= Border.Show) {
				return setTimerStarted(true)
			}

			const delay = diff - Border.Show
			const timer = setTimeout(() => {
				setTimerStarted(true)
			}, delay)

			return () => clearTimeout(timer)
		}
	}, [expired])

	useEffect(() => {
		if (!expired) {
			return
		}
		const delay = appDay(expired).diff(Date.now(), 'ms') + Border.Reload

		if (delay <= 0) {
			logOutRef.current()
			return
		}

		const timer = setTimeout(logOutRef.current, delay)
		return () => clearTimeout(timer)
	}, [expired, logOutRef])

	return (
		<div className='header__subscribe-accent'>
			{timerStarted && <Timer />}

			<LoadingButton
				className={css.logout}
				isLoading={isLoading}
				onClick={logOut}
				variant='bordered-dark'
			>
				Stop session
			</LoadingButton>
		</div>
	)
}

function Timer() {
	const expired = useAppSelector(selectExpired)
	const [showed, setShowed] = useState(0)

	useEffect(() => {
		function updateUI() {
			const minutes = appDay(expired).diff(Date.now(), 'minutes')
			setShowed(minutes)
		}

		updateUI()
		const interval = setInterval(updateUI, 1_000)

		return () => clearInterval(interval)
	}, [expired])

	return (
		<>
			<img alt='timer' src={timer} />
			<span className='header__minutes'>{showed} min</span>{' '}
			<span className='header__line'>|</span>
		</>
	)
}
